import type { Decor, Rect } from '../world/mapdef';
import { block, ellipse, rect, shadow, shade, type Ctx } from './paint';
import { registerObjectPrefixDrawer, registerPropDrawers } from './propRegistry';

/**
 * Drawers of the airport package: the terminal's furniture (`ap_*` decor) and the shared
 * city services every map can place — public toilets (`wc_*`), rent-a-ride shops (`rent_*`),
 * the bank counter and cash machines (`bank_*`).
 */
const CJK = '"PingFang TC", "Noto Sans TC", "Noto Sans CJK TC", "Microsoft JhengHei", sans-serif';

function txt(c: Ctx, s: string, x: number, y: number, size: number, col: string, weight = 900): void {
  c.font = `${weight} ${size}px ${CJK}`;
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  c.fillStyle = col;
  c.fillText(s, x, y);
}

function vgrad(c: Ctx, x: number, y: number, w: number, h: number, top: string, bottom: string): void {
  const g = c.createLinearGradient(0, y, 0, y + h);
  g.addColorStop(0, top);
  g.addColorStop(1, bottom);
  c.fillStyle = g;
  c.fillRect(x, y, w, h);
}

// --- Terminal decor -----------------------------------------------------------------------

function decor(c: Ctx, d: Decor, t: number): boolean {
  const { x, y } = d;
  switch (d.kind) {
    case 'ap_sign': {
      // Overhead airport sign: yellow text on black, Chinese + English, pictogram.
      const w = d.w ?? 96;
      block(c, x, y + 1, w, 13, '#15171c', '#2a2d35', '#0a0b0e');
      const label = d.variant === 1 ? ['登機門', 'Gates', '✈'] : ['入境 · 行李', 'Arrivals · Baggage', '🧳'];
      txt(c, `${label[2]} ${label[0]}`, x + w / 2, y + 5, 5.5, '#ffd84a');
      txt(c, label[1], x + w / 2, y + 10.5, 3.6, '#ffffff', 700);
      rect(c, '#ffd84a', x + 2, y + 13, w - 4, 0.6);
      return true;
    }
    case 'ap_plane': {
      // Big window onto the apron: a jet at the jet bridge, its beacon blinking.
      const w = d.w ?? 160, h = 30;
      vgrad(c, x, y, w, h, '#9fd3f0', '#dff2ff');
      rect(c, '#7f8a92', x, y + h - 7, w, 7);
      for (let i = 0; i < w; i += 14) rect(c, '#f4f1ea', x + i + 2, y + h - 4, 7, 0.8);
      // Fuselage, wing, tail, engines.
      const px0 = x + w * 0.2, py = y + h - 14;
      c.fillStyle = '#f4f6f8';
      c.beginPath();
      c.ellipse(px0 + w * 0.3, py, w * 0.3, 4.5, 0, 0, Math.PI * 2);
      c.fill();
      rect(c, '#2f4a8a', px0 + 2, py - 1.2, w * 0.56, 1.2);
      c.fillStyle = '#e2493b';
      c.beginPath();
      c.moveTo(px0 + w * 0.52, py - 3);
      c.lineTo(px0 + w * 0.6, py - 14);
      c.lineTo(px0 + w * 0.64, py - 14);
      c.lineTo(px0 + w * 0.6, py - 3);
      c.fill();
      c.fillStyle = '#d8dde3';
      c.beginPath();
      c.moveTo(px0 + w * 0.22, py + 1);
      c.lineTo(px0 + w * 0.08, py + 9);
      c.lineTo(px0 + w * 0.16, py + 9);
      c.lineTo(px0 + w * 0.34, py + 1);
      c.fill();
      block(c, px0 + w * 0.12, py + 5, 7, 3.5, '#8a9096');
      for (let i = 0; i < 9; i++) rect(c, '#2a3440', px0 + w * 0.12 + i * 5, py - 2, 1.6, 1.4);
      // Jet bridge from the window to the door.
      rect(c, '#a8aeb3', x + w * 0.02, py - 6, w * 0.2, 5);
      rect(c, '#7f8a92', x + w * 0.02, py - 1, w * 0.2, 1);
      if (Math.floor(t * 2) % 2 === 0) ellipse(c, '#ff5a4a', px0 + w * 0.3, py - 5.5, 1.2, 1.2);
      // Window frame mullions.
      for (let i = 0; i <= w; i += w / 4) rect(c, '#dfe4ea', x + i - 0.75, y, 1.5, h);
      rect(c, '#dfe4ea', x, y + h - 1, w, 1);
      return true;
    }
    case 'ap_counter': {
      // Check-in desks: a long white counter with screens, airline logo and queue ropes.
      const w = d.w ?? 128;
      const x0 = x - w / 2;
      shadow(c, x, y, w / 2, 4);
      block(c, x0, y - 16, w, 16, '#f4f6f8', '#ffffff', '#c9d0d6');
      rect(c, '#2f4a8a', x0, y - 16, w, 2.5);
      for (let i = 0; i < w / 32; i++) {
        const sx = x0 + 6 + i * 32;
        block(c, sx, y - 27, 12, 9, '#2b3440');
        rect(c, '#58b6f0', sx + 1, y - 26, 10, 7);
        rect(c, '#ffffff', sx + 2, y - 24, 6, 0.8);
        block(c, sx + 16, y - 24, 10, 7, '#ffffff');
        txt(c, `${i + 1}`, sx + 21, y - 20.5, 5, '#2f4a8a');
      }
      txt(c, '報到 Check-in', x, y - 14.5, 4.2, '#2f4a8a', 800);
      return true;
    }
    case 'ap_belt': {
      // Baggage carousel: a dark oval belt with suitcases going round.
      const w = d.w ?? 128;

      shadow(c, x, y, w / 2, 5);
      c.fillStyle = '#2a2d35';
      c.beginPath();
      c.ellipse(x, y - 5, w / 2, 9, 0, 0, Math.PI * 2);
      c.fill();
      c.fillStyle = '#9a9ea6';
      c.beginPath();
      c.ellipse(x, y - 5, w / 2 - 5, 5.5, 0, 0, Math.PI * 2);
      c.fill();
      const cols = ['#e2493b', '#3a8fd9', '#1b1d24', '#ffd34e', '#9b5fc0'];
      for (let i = 0; i < 5; i++) {
        const a = t * 0.5 + (i / 5) * Math.PI * 2;
        const bx = x + Math.cos(a) * (w / 2 - 7), by = y - 5 + Math.sin(a) * 6.5;
        block(c, bx - 4, by - 5, 8, 5, cols[i]);
        rect(c, 'rgba(0,0,0,0.25)', bx - 1, by - 6.2, 2, 1.2);
      }
      return true;
    }
    case 'ap_trolleys': {
      // A row of luggage trolleys nested together.
      shadow(c, x + 10, y, 12, 2.5);
      for (let i = 0; i < 4; i++) {
        const tx = x + i * 5;
        rect(c, '#9a9ea6', tx, y - 12, 1, 12);
        rect(c, '#9a9ea6', tx + 1, y - 12, 5, 1);
        rect(c, '#6a6f78', tx, y - 3, 7, 1.2);
        ellipse(c, '#1b1d24', tx + 1, y, 1.2, 1.2);
        ellipse(c, '#1b1d24', tx + 6, y, 1.2, 1.2);
      }
      return true;
    }
  }
  return false;
}

registerPropDrawers({ decor, animatedDecor: ['ap_plane', 'ap_belt'] });

// --- Shared services: object drawers by id prefix --------------------------------------------

/** A small public toilet block: tiled walls, blue/red door signs, a vent on the roof. */
function toiletBlock(c: Ctx, r: Rect): void {
  const x = r.x, w = r.w, y = r.y + r.h;
  shadow(c, x + w / 2, y, w / 2 + 2, 3);
  block(c, x - 2, y - 30, w + 4, 30, '#e9eef2', '#ffffff', '#b8c0c8');
  for (let i = 2; i < w + 2; i += 5) rect(c, 'rgba(0,0,0,0.06)', x - 2 + i, y - 28, 0.5, 26);
  block(c, x - 3, y - 34, w + 6, 5, '#7f8a92', '#9aa4ab', '#5a646c');
  rect(c, '#5a646c', x + w / 2 - 2, y - 37, 4, 3);
  const dw = Math.max(5, (w - 6) / 2);
  block(c, x + 1, y - 18, dw, 18, '#3a8fd9', '#6fb4f0', '#2a6fb0');
  block(c, x + w - 1 - dw, y - 18, dw, 18, '#e2493b', '#ff7a6a', '#a8261c');
  rect(c, '#ffffff', x + 1 + dw / 2 - 1, y - 24, 2, 4);
  ellipse(c, '#ffffff', x + 1 + dw / 2, y - 26, 1.3, 1.3);
  rect(c, '#ffffff', x + w - 1 - dw / 2 - 1, y - 24, 2, 4);
  ellipse(c, '#ffffff', x + w - 1 - dw / 2, y - 26, 1.3, 1.3);
  c.fillStyle = '#ffffff';
  c.beginPath();
  c.moveTo(x + w - 1 - dw / 2 - 2.2, y - 20);
  c.lineTo(x + w - 1 - dw / 2 + 2.2, y - 20);
  c.lineTo(x + w - 1 - dw / 2, y - 24);
  c.fill();
  txt(c, '🚻', x + w / 2, y - 31, 5, '#ffffff');
}

/** Rent-a-ride: a kiosk with a striped awning and a bike, a kick scooter and an e-bike lined up. */
function rentShop(c: Ctx, r: Rect, t: number): void {
  const x = r.x, w = r.w, y = r.y + r.h;
  shadow(c, x + w / 2, y, w / 2 + 6, 3.5);
  block(c, x, y - 26, w, 26, '#3fa36f', '#6fc48f', '#2a7a50');
  rect(c, '#ffffff', x + 3, y - 21, w - 6, 9);
  txt(c, '租車', x + w / 2, y - 18.5, 5.5, '#2a7a50');
  txt(c, 'RENT', x + w / 2, y - 13.8, 3.4, '#2a7a50', 800);
  // Awning.
  for (let i = 0; i < w + 8; i += 6) rect(c, i % 12 ? '#ffffff' : '#e2493b', x - 4 + i, y - 30, 6, 4);
  rect(c, 'rgba(0,0,0,0.15)', x - 4, y - 26, w + 8, 1);
  // Three rides on a rack in front, bobbing a little.
  const bob = Math.sin(t * 2) * 0.3;
  const bx = x - 2;
  ellipse(c, '#1b1d24', bx + 2, y + 2, 2.4, 2.4);
  ellipse(c, '#1b1d24', bx + 9, y + 2, 2.4, 2.4);
  rect(c, '#f08a24', bx + 2, y - 3 + bob, 7, 1.2);
  rect(c, '#f08a24', bx + 9, y - 7 + bob, 1, 5);
  const kx = x + w / 2 - 2;
  rect(c, '#2a2a30', kx, y - 1 + bob, 8, 1.2);
  rect(c, '#9a9ea6', kx + 7, y - 10 + bob, 1, 9);
  ellipse(c, '#1b1d24', kx + 1, y + 1, 1.6, 1.6);
  ellipse(c, '#1b1d24', kx + 7, y + 1, 1.6, 1.6);
  const ex = x + w - 8;
  block(c, ex, y - 7 + bob, 10, 5, '#e2493b', '#ff7a6a', '#a8261c');
  ellipse(c, '#1b1d24', ex + 1, y + 1, 2.6, 2.6);
  ellipse(c, '#1b1d24', ex + 9, y + 1, 2.6, 2.6);
  rect(c, '#1b1d24', ex + 2, y - 9 + bob, 5, 2);
}

/** The bank: a solid stone front with brass doors and a clock; cash machines are a blue box. */
function bank(c: Ctx, r: Rect, t: number): void {
  const x = r.x, w = r.w, y = r.y + r.h;
  if (w < 40) {
    // ATM.
    shadow(c, x + w / 2, y, w / 2 + 1, 3);
    block(c, x - 1, y - 30, w + 2, 30, '#2f5a9a', '#4a78b8', '#1e3f6e');
    rect(c, '#e9eef2', x + 1, y - 27, w - 2, 7);
    txt(c, 'ATM', x + w / 2, y - 23.5, 4.5, '#2f5a9a');
    block(c, x + 2, y - 18, w - 4, 7, '#1b2430');
    rect(c, `rgba(120,200,255,${0.6 + 0.3 * Math.sin(t * 3)})`, x + 3, y - 17, w - 6, 5);
    rect(c, '#9a9ea6', x + 3, y - 8, w - 6, 1.5);
    rect(c, '#ffd84a', x + 3, y - 5, 4, 1.2);
    return;
  }
  shadow(c, x + w / 2, y, w / 2 + 4, 4);
  block(c, x - 4, y - 40, w + 8, 40, '#d9d2c2', '#efe9dc', '#a89f8c');
  for (let i = 0; i < w + 8; i += 8) for (let j = 0; j < 40; j += 6) rect(c, 'rgba(0,0,0,0.05)', x - 4 + i + ((j / 6) % 2) * 4, y - 40 + j, 7, 0.6);
  // Pediment, columns, doors, clock, sign.
  c.fillStyle = '#c9c0ad';
  c.beginPath();
  c.moveTo(x - 6, y - 40);
  c.lineTo(x + w / 2, y - 48);
  c.lineTo(x + w + 6, y - 40);
  c.fill();
  for (const cx of [x + 2, x + w - 6]) block(c, cx, y - 36, 4, 36, '#efe9dc', '#ffffff', '#c9c0ad');
  block(c, x + w / 2 - 7, y - 20, 14, 20, '#b8862a', '#e0b23a', '#8a6210');
  rect(c, '#6a4a10', x + w / 2 - 0.5, y - 19, 1, 18);
  ellipse(c, '#ffffff', x + w / 2, y - 31, 4, 4);
  ellipse(c, '#2a2a30', x + w / 2, y - 31, 3.4, 3.4);
  ellipse(c, '#ffffff', x + w / 2, y - 31, 2.8, 2.8);
  rect(c, '#2a2a30', x + w / 2 - 0.3, y - 33.5, 0.6, 2.8);
  rect(c, '#2a2a30', x + w / 2, y - 31.3, 2, 0.6);
  block(c, x + 2, y - 46, w - 4, 6, '#2f5a9a', '#4a78b8', '#1e3f6e');
  txt(c, '燈籠銀行 · BANK', x + w / 2, y - 43, 4.2, '#ffd84a');
  rect(c, shade('#2f5a9a', -0.2), x + 2, y - 40, w - 4, 0.8);
}

registerObjectPrefixDrawer('wc_', toiletBlock);
registerObjectPrefixDrawer('rent_', rentShop);
registerObjectPrefixDrawer('bank_', bank);
registerPropDrawers({ animatedObjects: ['rent_town', 'rent_taipei', 'rent_hualien', 'rent_beitou', 'rent_cks', 'bank_atmTaipei', 'bank_atmHualien', 'bank_atmAirport'] });

// The airport's own objects reuse the kiosk/sign looks.
registerPropDrawers({
  objects: {
    ap_mrtTaipei(c, r) {
      const x = r.x, w = r.w, y = r.y + r.h;
      shadow(c, x + w / 2, y, w / 2 + 2, 3);
      block(c, x - 2, y - 24, w + 4, 24, '#6a3fa0', '#8f63c8', '#4a2a78');
      rect(c, '#ffffff', x, y - 21, w, 8);
      txt(c, '機場捷運', x + w / 2, y - 18.5, 4.6, '#6a3fa0');
      txt(c, 'Airport MRT', x + w / 2, y - 14.5, 3.2, '#6a3fa0', 800);
      block(c, x + 2, y - 12, w - 4, 12, '#1b1d24');
      txt(c, '✈', x + w / 2, y - 6, 7, '#ffd84a');
    },
    ap_gate(c, r) {
      const x = r.x, w = r.w, y = r.y + r.h;
      block(c, x, y - 14, w, 14, '#e9eef2', '#ffffff', '#b8c0c8');
      block(c, x + w / 2 - 20, y - 26, 40, 11, '#15171c', '#2a2d35', '#0a0b0e');
      txt(c, 'C7 · BARCELONA', x + w / 2, y - 22.5, 4, '#ffd84a');
      txt(c, 'BR 67 · BOARDING', x + w / 2, y - 17.5, 3, '#6fe09c', 800);
      rect(c, '#2f4a8a', x + 4, y - 4, w - 8, 1.5);
      for (let i = 0; i < 3; i++) block(c, x + 6 + i * 12, y - 10, 8, 6, '#2f4a8a');
    },
    ap_info(c, r) {
      const x = r.x, w = r.w, y = r.y + r.h;
      shadow(c, x + w / 2, y, w / 2 + 2, 3);
      block(c, x, y - 16, w, 16, '#f4f6f8', '#ffffff', '#c9d0d6');
      block(c, x + w / 2 - 7, y - 30, 14, 12, '#2f5a9a', '#4a78b8', '#1e3f6e');
      txt(c, 'i', x + w / 2, y - 24, 9, '#ffffff');
    },
    ap_duty(c, r) {
      const x = r.x, w = r.w, y = r.y + r.h;
      shadow(c, x + w / 2, y, w / 2 + 2, 3);
      block(c, x, y - 28, w, 28, '#f4f6f8', '#ffffff', '#c9d0d6');
      block(c, x, y - 32, w, 5, '#c8901a', '#ffd84a', '#8a6210');
      txt(c, 'DUTY FREE 免稅', x + w / 2, y - 29.5, 3.6, '#2a1805');
      for (let i = 0; i < 3; i++) {
        rect(c, '#e8d8b0', x + 3, y - 22 + i * 7, w - 6, 1);
        for (let k = 0; k < (w - 6) / 7; k++) block(c, x + 4 + k * 7, y - 27 + i * 7, 5, 5, ['#ffd34e', '#e2493b', '#3fa36f', '#9b5fc0'][(i + k) % 4]);
      }
    },
    ap_security(c, r) {
      const x = r.x, y = r.y + r.h;
      block(c, x - 6, y - r.h - 10, r.w + 12, 8, '#7f8a92', '#9aa4ab', '#5a646c');
      rect(c, '#1b1d24', x - 4, y - r.h - 8, r.w + 8, 4);
      txt(c, '安檢 SECURITY', x + r.w / 2, y - r.h - 6, 3, '#ffd84a');
      rect(c, 'rgba(255,216,74,0.35)', x, y - r.h, r.w, r.h);
    },
  },
});
